import { useLayoutEffect, useRef } from 'react'

import { css } from '@emotion/react'
import type { Options as FocusTrapOptions } from 'focus-trap'
import { createFocusTrap } from 'focus-trap'

interface IProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean
  /** When true, listen on the shadow host's ownerDocument (needed to trap Tab against the host page). */
  listenOnOwnerDocument?: boolean
  options?: FocusTrapOptions
  onEnter?: (container: HTMLDivElement) => void
  onExit?: (container: HTMLDivElement) => void
  /**
   * Delay in ms to wait before firing checkCanFocusTrap. `0` skips the check
   * altogether so the trap starts listening synchronously, which matters when
   * another trap is nested inside this one: `focus-trap`'s queue pauses
   * whichever trap is on top when a new one registers, so a deferred
   * registration would pause a nested trap that had already activated.
   */
  focusCheckDelay?: number
  fullHeight?: boolean
}

/**
 * Resolves the document a focus trap should listen on. When the element lives
 * inside a shadow root and `listenOnOwnerDocument` is set, returns the shadow
 * host's owner document so Tab can be trapped against the host page.
 */
export function getFocusTrapDocument(
  element: HTMLElement,
  listenOnOwnerDocument?: boolean,
): Document {
  const rootNode = element.getRootNode()

  if (listenOnOwnerDocument && rootNode instanceof ShadowRoot) {
    return rootNode.host.ownerDocument
  }

  return rootNode as Document
}

/**
 *
 */
export const FocusTrap: React.FC<IProps> = (props) => {
  const {
    children,
    disabled,
    listenOnOwnerDocument,
    options,
    onEnter,
    onExit,
    focusCheckDelay = 750,
    fullHeight = true,
    ...rest
  } = props

  const ref = useRef<HTMLDivElement>(null)
  const trapRef = useRef<ReturnType<typeof createFocusTrap> | null>(null)
  useLayoutEffect(() => {
    if (ref.current) {
      trapRef.current = createFocusTrap(ref.current, {
        clickOutsideDeactivates: true,
        fallbackFocus: ref.current,
        preventScroll: true,
        checkCanFocusTrap: focusCheckDelay
          ? () =>
              new Promise((resolve) => {
                setTimeout(resolve, focusCheckDelay)
              })
          : undefined,
        document: getFocusTrapDocument(ref.current, listenOnOwnerDocument),
        ...options,
      })
    }
  }, [focusCheckDelay, listenOnOwnerDocument, options])

  useLayoutEffect(() => {
    const container = ref.current
    if (disabled) {
      trapRef.current?.deactivate()
      onExit && container && onExit(container)
    } else {
      if (trapRef.current && container) {
        trapRef.current.activate()
        onEnter && onEnter(container)
      }
    }
  }, [disabled, onEnter, onExit])

  useLayoutEffect(
    () => () => {
      trapRef.current?.deactivate()
      onExit && ref.current && onExit(ref.current)
    },
    [onExit],
  )

  return (
    <div
      css={
        fullHeight
          ? css`
              height: 100%;
            `
          : undefined
      }
      className="cy-focustrap"
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  )
}
